import React, { useEffect, useState } from 'react';
import './SearchBar.css'; // Importamos el archivo CSS para los estilos

/**
 * Componente de Barra de Búsqueda.
 * @param {string} searchQuery - Consulta de búsqueda actual.
 * @param {function} setSearchQuery - Función para actualizar la consulta de búsqueda.
 * @param {function} onSearch - Función para ejecutar la búsqueda cuando se haga clic en el botón.
 * @param {Array} products - Lista de productos para extraer nombres de placeholders.
 * @param {boolean} noResults - Indica si la última búsqueda no tuvo resultados.
 * @param {function} onCancel - Función para cancelar la búsqueda y limpiar el buscador.
 */
const SearchBar = ({ searchQuery, setSearchQuery, onSearch, products = [], noResults, onCancel }) => {
    const [isFocused, setIsFocused] = useState(false); // Estado para manejar el enfoque
    const [placeholder, setPlaceholder] = useState('Buscar productos...');

    // Obtener nombres únicos de productos para evitar duplicados en placeholders
    const placeholderOptions = Array.isArray(products) && products.length > 0
        ? [...new Set(products.map(product => product.name))]
        : [];

    useEffect(() => {
        let placeholderInterval;

        // Función para seleccionar un placeholder aleatorio
        const getRandomPlaceholder = () => {
            if (placeholderOptions.length === 0) return 'Buscar productos...';
            const randomIndex = Math.floor(Math.random() * placeholderOptions.length);
            return placeholderOptions[randomIndex];
        };

        if (!isFocused) {
            // Inicializar el placeholder con un valor aleatorio
            setPlaceholder(getRandomPlaceholder());

            // Cambiar el placeholder cada 3 segundos
            placeholderInterval = setInterval(() => {
                setPlaceholder(getRandomPlaceholder());
            }, 3000);
        } else {
            // Cuando está enfocado, mostrar el placeholder fijo
            setPlaceholder('Buscar productos...');
        }

        // Limpiar el intervalo al desmontar o al cambiar el enfoque
        return () => {
            if (placeholderInterval) {
                clearInterval(placeholderInterval);
            }
        };
    }, [isFocused, placeholderOptions]);

    /**
     * Maneja el cambio en el campo de entrada.
     * @param {object} e - Evento de cambio.
     */
    const handleInputChange = (e) => {
        setSearchQuery(e.target.value);
    };

    /**
     * Maneja el clic en el botón de búsqueda.
     */
    const handleSearch = () => {
        if (onSearch) {
            let query = searchQuery.trim();
            if (query === '' && placeholder !== 'Buscar productos...') {
                query = placeholder;
            }
            onSearch(query);
        }
    };

    /**
     * Maneja el evento de tecla presionada en el input para que 'Enter' funcione como buscar.
     * @param {object} e - Evento de tecla presionada.
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch(); // Ejecuta la búsqueda cuando se presiona 'Enter'
        }
    };

    /**
     * Maneja el clic en el botón de cancelación.
     * @param {object} e - Evento de mousedown.
     */
    const handleCancel = (e) => {
        e.preventDefault(); // Prevenir que el input pierda el enfoque inmediatamente
        setSearchQuery('');
        onCancel();
    };

    return (
        <div className="search-bar">
            <div className="search-input-container">
                {/* Botón de cancelación */}
                {(isFocused || noResults) && (
                    <button
                        type="button"
                        className="cancel-button"
                        aria-label="Cancelar búsqueda"
                        onMouseDown={handleCancel} // Usar onMouseDown en lugar de onClick
                    >
                        &lt;
                    </button>
                )}
                <input
                    type="text"
                    placeholder={placeholder}
                    value={searchQuery}
                    onChange={handleInputChange}
                    className="search-input"
                    aria-label="Buscar productos"
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={handleKeyDown} // Detecta la tecla 'Enter' para buscar
                />
                <button
                    type="button"
                    className="search-button"
                    aria-label="Buscar"
                    onClick={handleSearch}
                >
                    <img src="/images/lupa.png" alt="Buscar" className="search-icon" />
                </button>
            </div>
        </div>
    );
};

export default SearchBar;
